import React, { useState, useEffect, useCallback, useContext, Fragment } from 'react'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { useRouter } from 'next/router'
import { downloadAllAlbums } from '../../../api/user/profile-api'
import { openModal } from '../../../service/modal-service'
import { UserContext } from '../../../context/UserContext'
import { LoadingCover } from '../../commons/LoadingCover'
import { SystemError } from '../../error/SystemError'
import { AnimatePresence, motion } from 'framer-motion'
import { NewAlbum } from './NewAlbum'
import { Menu, Transition } from '@headlessui/react'
import { AlbumDelete } from './AlbumDelete'
import { AlbumPrivacy } from './AlbumPrivacy'
import { AddPhotos } from './AddPhotos'
import ReactPlayer from 'react-player'
import { ChevronLeft, Plus, MoreVertical, ImagePlus, Trash2, Lock, FolderOpen, Loader2 } from 'lucide-react'

export const Photos = (props) => {
    const router = useRouter()
    const stringHandleName = props.stringHandleName
    const stringsUserId = props.stringsUserId

    const [ displayAlbums, setDisplayAlbums ] = useState({})
    const [ isLoadingAlbums, setIsLoadingAlbums ] = useState(true)

    const [ isShowNewAlbum, setIsShowNewAlbum ] = useState(false)
    const [ isShowAlbumDelete, setIsShowAlbumDelete ] = useState(false)
    const [ isShowAlbumPrivacy, setIsShowAlbumPrivacy ] = useState(false)
    const [ isShowAddPhoto, setIsShowAddPhoto ] = useState(false)

    const [ editAlbumName, setEditAlbumName ] = useState("")

    const [ isLoading, setIsLoading ] = useState(false);
    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}); 

    const { userId } = useContext(UserContext)

    const getAlbums = useCallback(async() => {
        setIsLoadingAlbums(true)
        try {
            const photos = await downloadAllAlbums(!userId? "" : userId, stringsUserId)
            const albumNames = [...new Set(photos.map(item => item.albumName))];
            let albumObject = {}
            for(let x in albumNames){
                let filteredPhotos = photos.filter(
                    (photos) => photos.albumName === albumNames[x]
                )
                albumObject = {...albumObject, [albumNames[x]] : filteredPhotos}
            }
            setDisplayAlbums(albumObject)
        } catch (error) {
            console.log(error);
            setErrorContent({'header': "Error getting albums", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
        }
        setIsLoadingAlbums(false)
    }, [stringsUserId, userId])

    useEffect(() => {
        if(!stringsUserId) return
        getAlbums()      
    }, [stringsUserId, getAlbums])

    const changeRoute = (path) =>{
        // setIsLoading(true)
        router.push(path).then(() => {
            setIsLoading(false)
        })
    }
    
    const deleteAlbum = (albumName) => {
        setEditAlbumName(albumName)
        openModal(setIsShowAlbumDelete)
    }

    const editPrivacy = (albumName) => {
        setEditAlbumName(albumName)
        openModal(setIsShowAlbumPrivacy)
    }

    const addPhotos = (albumName) => {
        setEditAlbumName(albumName)
        openModal(setIsShowAddPhoto)
    }

  return (
    <div className='bg-gray-50 min-h-screen'>
        {/* Header */}
        <div className='bg-white border-b border-gray-100 p-4'>
            {userId === stringsUserId && (
                <motion.button 
                    whileTap={{ scale: 0.95 }}
                    onClick={() => openModal(setIsShowNewAlbum)}
                    className='flex items-center gap-1 px-4 py-2 bg-linear-to-r from-orange-500 to-amber-500 text-white text-sm font-medium rounded-xl shadow-sm'
                >
                    <Plus size={18} />
                    New Album
                </motion.button>
            )}
        </div>

        <div className='p-4 pb-24'>
            {/* Loading State */}
            {isLoadingAlbums && (
                <div className='flex justify-center py-12'>
                    <Loader2 size={24} className='text-orange-500 animate-spin' />
                </div>
            )}

            {/* Empty State */}
            {!isLoadingAlbums && Object.keys(displayAlbums).length === 0 && (
                <div className='text-center py-16'>
                    <div className='w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center'>
                        <FolderOpen size={32} className='text-gray-400' />
                    </div>
                    <p className='text-gray-500 font-medium'>No albums yet</p>
                    <p className='text-sm text-gray-400 mt-1'>Create your first album to share photos</p>
                </div>
            )}

            {/* Albums Grid */}
            {!isLoadingAlbums && Object.keys(displayAlbums).length > 0 && (
                <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                    {Object.keys(displayAlbums).map((albumName, i) => (
                        <motion.div 
                            key={i}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.05 }}
                            className='relative group'
                        >
                            <div className='aspect-square rounded-2xl bg-gray-100 shadow-sm relative'>
                                {/* Image/Video Container with overflow hidden for rounded corners */}
                                <div className='absolute inset-0 rounded-2xl overflow-hidden'>
                                    {displayAlbums[albumName][0].mediaType.includes("image") && (
                                        <Image 
                                            className='w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105'
                                            src={displayAlbums[albumName][0].Image}
                                            fill
                                            alt="album"
                                            quality={75}
                                            sizes="(max-width: 768px) 33vw, 33vw"
                                            placeholder="blur"
                                            blurDataURL={s3Static + "placeholder.png"}
                                            onClick={() => changeRoute("/profile/" + stringHandleName + "/photos/" + albumName)}
                                        />  
                                    )}
                                    {displayAlbums[albumName][0].mediaType.includes("video") && (
                                        <div 
                                            className='absolute inset-0 cursor-pointer'
                                            onClick={() => changeRoute("/profile/" + stringHandleName + "/photos/" + albumName)}
                                        >
                                            <video
                                                src={displayAlbums[albumName][0].Image}
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                className='w-full h-full object-cover'
                                            />
                                        </div>
                                    )}
                                    
                                    {/* Album Name Overlay */}
                                    <div 
                                        className='absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent cursor-pointer'
                                        onClick={() => changeRoute("/profile/" + stringHandleName + "/photos/" + albumName)}
                                    >
                                        <div className='absolute bottom-0 left-0 right-0 p-3'>
                                            <span className='text-white font-semibold text-sm truncate block'>
                                                {albumName}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Owner Menu - Outside overflow-hidden container */}
                                {(userId === stringsUserId) && (
                                    <div className='absolute top-2 right-2 z-10'>
                                        <Menu as="div" className="relative">
                                            <Menu.Button className="p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors">
                                                <MoreVertical size={18} className='text-white' />
                                            </Menu.Button>
                                            <Transition
                                                as={Fragment}
                                                enter="transition ease-out duration-200"
                                                enterFrom="transform opacity-0 scale-95"
                                                enterTo="transform opacity-100 scale-100"
                                                leave="transition ease-in duration-150"
                                                leaveFrom="transform opacity-100 scale-100"
                                                leaveTo="transform opacity-0 scale-95"
                                            >
                                                <Menu.Items className='absolute right-0 mt-2 w-38 bg-white rounded-xl shadow-lg border border-gray-100 focus:outline-none z-20'>                                            
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <button 
                                                                onClick={() => addPhotos(albumName)}
                                                                className={`${active ? 'bg-gray-50' : ''} flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 rounded-t-xl`}
                                                            >
                                                                <ImagePlus size={16} className='text-gray-400' />
                                                                Add Photos
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                    {(albumName !== "Profile" && albumName !== "Carousel") && (
                                                        <>
                                                            <Menu.Item>
                                                                {({ active }) => (
                                                                    <button 
                                                                        onClick={() => editPrivacy(albumName)}
                                                                        className={`${active ? 'bg-gray-50' : ''} flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 border-t border-gray-50`}
                                                                    >
                                                                        <Lock size={16} className='text-gray-400' />
                                                                        Edit Privacy
                                                                    </button>
                                                                )}
                                                            </Menu.Item>
                                                            <Menu.Item>
                                                                {({ active }) => (
                                                                    <button 
                                                                        onClick={() => deleteAlbum(albumName)}
                                                                        className={`${active ? 'bg-red-50' : ''} flex items-center gap-2 w-full px-4 py-3 text-sm text-red-600 border-t border-gray-50 rounded-b-xl`}
                                                                    >
                                                                        <Trash2 size={16} />
                                                                        Delete Album
                                                                    </button>
                                                                )}
                                                            </Menu.Item>
                                                        </>
                                                    )}
                                                </Menu.Items>
                                            </Transition>
                                        </Menu> 
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>

        <AnimatePresence>
            {isShowNewAlbum && <NewAlbum getAlbums={getAlbums}/>}
        </AnimatePresence>
        <AnimatePresence>
            {isShowAddPhoto && <AddPhotos getAlbums={getAlbums} editAlbumName={editAlbumName}/>}
        </AnimatePresence>
        <AnimatePresence>
            {isShowAlbumDelete && <AlbumDelete getAlbums={getAlbums} editAlbumName={editAlbumName}/>}
        </AnimatePresence>
        <AnimatePresence>
            {isShowAlbumPrivacy && <AlbumPrivacy getAlbums={getAlbums} editAlbumName={editAlbumName}/>}
        </AnimatePresence>
        {isLoading && <LoadingCover/>}
        {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
    </div>
  )
}
